import {
  OrganizationChooser,
  RequireInternalOrganization,
  WrongAppNotice,
  useRequireOrganization,
} from '@startupquickstart/frontend';

/** Where the hunts admin lives. Overridable per environment. */
const HUNTS_APP_URL = import.meta.env.VITE_HUNTS_APP_URL ?? 'https://admin.destinationhunts.com';

interface RequireToursAccessProps {
  children: React.ReactNode;
}

/**
 * Tours-internal route guard that explains itself.
 *
 * `RequireInternalOrganization` alone redirects a non-internal org to
 * `/dashboard`. For a hunts-only account — a BCE staffer with
 * `org:hunts_admin`, say — that means landing on a page they have no
 * rights to and concluding the product is broken. Their account is fine;
 * they are at the wrong door.
 *
 * So the redirect is replaced with a notice naming the product they *can*
 * use, plus a chooser: a user with several organizations may simply have
 * the wrong one active, which is a different problem from having no
 * access at all.
 *
 * This is signposting, not enforcement. What a caller may actually read or
 * change is decided server-side per request.
 */
export function RequireToursAccess({ children }: RequireToursAccessProps) {
  // The DB record, not the Clerk one: this notice names the org the
  // user is actually acting as, and its name is always populated.
  const { dbOrganization } = useRequireOrganization();

  return (
    <RequireInternalOrganization
      noAccessFallback={
        <WrongAppNotice
          currentProductName="Destination Tours"
          availableProductName="Destination Hunts"
          availableProductUrl={HUNTS_APP_URL}
          organizationName={dbOrganization?.name}
        >
          <div className="text-left">
            <p className="mb-2 text-xs text-muted-foreground">
              Or switch to a different organization:
            </p>
            {/* autoSelectSingle off: a single-org user is already in the
                org that has no access, and re-entering it would loop. */}
            <OrganizationChooser autoSelectSingle={false} />
          </div>
        </WrongAppNotice>
      }
    >
      {children}
    </RequireInternalOrganization>
  );
}
